import { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERIOR_STYLES = [
  'Minimalist','Industrial','Scandinavian','Modern Classic',
  'Rustic','Contemporary','Art Deco','Other',
];

const SIGNAGE_TYPES = [
  'Exterior Fascia','Window Graphics','Interior Wayfinding',
  'LED/Neon','Vehicle Wrap','Hoarding','A-frame','Other',
];

const BUDGET_RANGES = [
  'Under $5K','$5K–$15K','$15K–$30K','$30K–$60K','$60K+','TBD',
];

const MARKETS = ['AU','LB','Both'];

const EMPTY_FORM = {
  interior_style: '',
  signage_types: [],
  site_address: '',
  site_size_sqm: '',
  primary_market: '',
  contractor_contact: '',
  budget_range: '',
  timeline: '',
  special_requirements: '',
};

const PhysicalBriefPanel = ({ projectId }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/projects/${projectId}/physical-brief`, { withCredentials: true });
        setForm({ ...EMPTY_FORM, ...res.data });
        setHasChanges(false);
      } catch {
        // 404 = brief not created yet — start from empty form, that's fine
        setForm(EMPTY_FORM);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId]);

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const toggleSignageType = (type) => {
    setForm(prev => {
      const types = prev.signage_types.includes(type)
        ? prev.signage_types.filter(t => t !== type)
        : [...prev.signage_types, type];
      return { ...prev, signage_types: types };
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/projects/${projectId}/physical-brief`,
        form,
        { withCredentials: true }
      );
      setHasChanges(false);
      toast.success('Physical brief saved');
    } catch {
      toast.error('Failed to save brief');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6" data-testid="physical-brief-panel">
      <div className="max-w-3xl mx-auto">
        <div>
          {/* Panel header */}
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl text-foreground mb-2">Physical Presence</h2>
              <p className="text-sm text-muted-foreground font-mono">
                BP-03-A — Physical brief for interior, signage, and built-environment work
              </p>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50 flex-shrink-0"
              style={{ borderRadius: 0 }}
              data-testid="physical-brief-save-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />Save Brief</>
              )}
            </Button>
          </div>

          <div className="glass border border-border p-6 space-y-6">
            {/* Interior Style */}
            <div className="space-y-2">
              <Label htmlFor="interior_style" className="font-mono text-xs uppercase tracking-wide text-foreground/60">
                Interior Style
              </Label>
              <select
                id="interior_style"
                value={form.interior_style}
                onChange={e => update('interior_style', e.target.value)}
                className="w-full bg-black/[0.03] border border-border text-foreground text-sm px-3 py-2 font-mono focus:outline-none focus:ring-1 focus:ring-primary/50"
                style={{ borderRadius: 0 }}
                data-testid="interior-style-select"
              >
                <option value="">— Select style —</option>
                {INTERIOR_STYLES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Signage Type — multi-checkbox */}
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wide text-foreground/60">
                Signage Type
              </Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {SIGNAGE_TYPES.map(type => {
                  const checked = form.signage_types.includes(type);
                  return (
                    <label
                      key={type}
                      className={`flex items-center gap-2 px-3 py-2 border cursor-pointer transition-colors ${
                        checked
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-border bg-black/[0.02] text-foreground/70 hover:bg-black/[0.04]'
                      }`}
                      style={{ borderRadius: 0 }}
                    >
                      {/* Square checkbox */}
                      <span
                        className="w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                        style={{
                          borderRadius: 0,
                          borderColor: checked ? '#C5A059' : 'rgba(27,42,61,0.35)',
                          backgroundColor: checked ? '#C5A059' : 'transparent',
                        }}
                      >
                        {checked && (
                          <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none">
                            <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSignageType(type)}
                        className="sr-only"
                        data-testid={`signage-${type.replace(/\s+/g, '-').toLowerCase()}`}
                      />
                      <span className="font-mono text-xs">{type}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Two-column grid for shorter fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Site Address */}
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="site_address" className="font-mono text-xs uppercase tracking-wide text-foreground/60">
                  Site Address
                </Label>
                <Input
                  id="site_address"
                  value={form.site_address}
                  onChange={e => update('site_address', e.target.value)}
                  placeholder="123 Example St, Melbourne VIC"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/60 font-mono text-sm"
                  style={{ borderRadius: 0 }}
                  data-testid="site-address-input"
                />
              </div>

              {/* Site Size */}
              <div className="space-y-2">
                <Label htmlFor="site_size_sqm" className="font-mono text-xs uppercase tracking-wide text-foreground/60">
                  Site Size (sqm)
                </Label>
                <Input
                  id="site_size_sqm"
                  value={form.site_size_sqm}
                  onChange={e => update('site_size_sqm', e.target.value)}
                  placeholder="e.g. 120"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/60 font-mono text-sm"
                  style={{ borderRadius: 0 }}
                  data-testid="site-size-input"
                />
              </div>

              {/* Primary Market */}
              <div className="space-y-2">
                <Label htmlFor="primary_market" className="font-mono text-xs uppercase tracking-wide text-foreground/60">
                  Primary Market
                </Label>
                <select
                  id="primary_market"
                  value={form.primary_market}
                  onChange={e => update('primary_market', e.target.value)}
                  className="w-full bg-black/[0.03] border border-border text-foreground text-sm px-3 py-2 font-mono focus:outline-none focus:ring-1 focus:ring-primary/50"
                  style={{ borderRadius: 0 }}
                  data-testid="primary-market-select"
                >
                  <option value="">— Select market —</option>
                  {MARKETS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              {/* Contractor Contact */}
              <div className="space-y-2">
                <Label htmlFor="contractor_contact" className="font-mono text-xs uppercase tracking-wide text-foreground/60">
                  Contractor Contact
                </Label>
                <Input
                  id="contractor_contact"
                  value={form.contractor_contact}
                  onChange={e => update('contractor_contact', e.target.value)}
                  placeholder="Name, phone or email"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/60 font-mono text-sm"
                  style={{ borderRadius: 0 }}
                  data-testid="contractor-contact-input"
                />
              </div>

              {/* Budget Range */}
              <div className="space-y-2">
                <Label htmlFor="budget_range" className="font-mono text-xs uppercase tracking-wide text-foreground/60">
                  Budget Range
                </Label>
                <select
                  id="budget_range"
                  value={form.budget_range}
                  onChange={e => update('budget_range', e.target.value)}
                  className="w-full bg-black/[0.03] border border-border text-foreground text-sm px-3 py-2 font-mono focus:outline-none focus:ring-1 focus:ring-primary/50"
                  style={{ borderRadius: 0 }}
                  data-testid="budget-range-select"
                >
                  <option value="">— Select range —</option>
                  {BUDGET_RANGES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              {/* Timeline */}
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="timeline" className="font-mono text-xs uppercase tracking-wide text-foreground/60">
                  Timeline
                </Label>
                <Input
                  id="timeline"
                  value={form.timeline}
                  onChange={e => update('timeline', e.target.value)}
                  placeholder="e.g. Q3 2026, or by 1 Oct"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/60 font-mono text-sm"
                  style={{ borderRadius: 0 }}
                  data-testid="timeline-input"
                />
              </div>
            </div>

            {/* Special Requirements */}
            <div className="space-y-2">
              <Label htmlFor="special_requirements" className="font-mono text-xs uppercase tracking-wide text-foreground/60">
                Special Requirements
              </Label>
              <textarea
                id="special_requirements"
                value={form.special_requirements}
                onChange={e => update('special_requirements', e.target.value)}
                rows={4}
                placeholder="Accessibility needs, heritage constraints, specific materials, council approvals required..."
                className="w-full bg-black/[0.03] border border-border text-foreground text-sm px-3 py-2 font-mono focus:outline-none focus:ring-1 focus:ring-primary/50 placeholder:text-muted-foreground/60 resize-none"
                style={{ borderRadius: 0 }}
                data-testid="special-requirements-input"
              />
            </div>

            {/* Inline save (bottom of form) */}
            <div className="pt-2 flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50"
                style={{ borderRadius: 0 }}
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />Save Brief</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhysicalBriefPanel;
